import React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { useStepIndicators, StepConfig } from './StepIndicators.hooks';
import {
  StepIndicatorsContainer,
  StepIndicatorItem,
  StepCircle,
  StepIndicatorText,
} from './StepIndicators.styles';

export interface StepIndicatorsProps {
  currentStep: number;
  totalSteps?: number;
  variant?: 'default' | 'small' | 'large';
  showConnectors?: boolean;
  className?: string;
  steps?: StepConfig[];
  onStepClick?: (step: number) => void;
}

const StepIndicators: React.FC<StepIndicatorsProps> = ({
  currentStep,
  totalSteps = 3,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  variant = 'default',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showConnectors = true,
  className,
  steps,
  onStepClick,
}) => {
  const theme = useTheme();

  const {
    steps: stepConfigs,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getStepClasses,
    isValidStep,
  } = useStepIndicators({
    currentStep,
    totalSteps,
    steps,
  });

  // Si el paso actual no es válido, no renderizar nada
  if (!isValidStep) {
    return null;
  }

  // Función para obtener el color del texto basado en el estado
  const getTextColor = (step: StepConfig, state: 'active' | 'completed' | 'inactive') => {
    const isDark = theme.palette.mode === 'dark';
    if (state === 'active') {
      return theme.palette.text.primary;
    }

    const colorMap: Record<string, string> = {
      'text-white': '#ffffff',
      'text-purple-800': isDark ? alpha('#c084fc', 0.95) : '#6b21a8',
      'text-purple-700': isDark ? alpha('#a78bfa', 0.95) : '#7c3aed',
      'text-blue-800': isDark ? alpha('#93c5fd', 0.95) : '#1e40af',
      'text-blue-700': isDark ? alpha('#60a5fa', 0.95) : '#1d4ed8',
      'text-emerald-800': isDark ? alpha('#6ee7b7', 0.95) : '#065f46',
      'text-emerald-700': isDark ? alpha('#34d399', 0.95) : '#047857',
      'text-gray-700': isDark ? theme.palette.text.secondary : '#374151',
      'text-gray-500': isDark ? theme.palette.text.secondary : '#6b7280',
    };

    const colorClass = step.textColor[state];
    return colorMap[colorClass] || (isDark ? theme.palette.text.secondary : '#374151');
  };

  // Función para obtener el color de fondo del círculo
  const getCircleBgColor = (step: StepConfig, state: 'active' | 'completed' | 'inactive') => {
    const isDark = theme.palette.mode === 'dark';
    const colorMap: Record<string, string> = {
      'bg-purple-600': '#9333ea',
      'bg-purple-100': isDark ? alpha('#9333ea', 0.28) : '#f3e8ff',
      'bg-blue-600': '#2563eb',
      'bg-blue-100': isDark ? alpha('#2563eb', 0.28) : '#dbeafe',
      'bg-emerald-500': '#10b981',
      'bg-emerald-100': isDark ? alpha('#10b981', 0.28) : '#d1fae5',
      'bg-gray-200': isDark ? theme.palette.divider : '#e5e7eb',
    };

    const colorClass = step.color[state];
    return colorMap[colorClass] || (isDark ? theme.palette.divider : '#e5e7eb');
  };

  // Función para obtener el color del icono
  const getIconColor = (step: StepConfig, state: 'active' | 'completed' | 'inactive') => {
    const isDark = theme.palette.mode === 'dark';
    if (state === 'active') return '#ffffff';
    if (state === 'completed') {
      if (step.color.completed.includes('purple')) return isDark ? '#e9d5ff' : '#6b21a8';
      if (step.color.completed.includes('blue')) return isDark ? '#bfdbfe' : '#1e40af';
      if (step.color.completed.includes('emerald')) return isDark ? '#a7f3d0' : '#065f46';
    }
    return isDark ? theme.palette.text.secondary : '#6b7280';
  };

  return (
    <StepIndicatorsContainer className={className}>
      {stepConfigs.map((step) => {
        const state =
          step.id === currentStep ? 'active' : step.id < currentStep ? 'completed' : 'inactive';
        const textColor = getTextColor(step, state);
        const circleBgColor = getCircleBgColor(step, state);
        const iconColor = getIconColor(step, state);
        const isActive = state === 'active';

        return (
          <StepIndicatorItem
            key={step.id}
            onClick={() => onStepClick?.(step.id)}
            sx={{
              cursor: onStepClick ? 'pointer' : 'default',
            }}
          >
            <StepCircle
              sx={{
                backgroundColor: circleBgColor,
                color: iconColor,
              }}
            >
              {step.icon}
            </StepCircle>
            <StepIndicatorText
              sx={{
                color: textColor,
                fontWeight: isActive ? 600 : 500,
              }}
            >
              {step.title}
            </StepIndicatorText>
          </StepIndicatorItem>
        );
      })}
    </StepIndicatorsContainer>
  );
};

export default StepIndicators;
